import React, { useEffect, useMemo, useState } from 'react';
import { Table, Input, Button, Dropdown, Menu, Checkbox, Steps, Space, Flex, Tag, TableColumnProps, Select, MenuProps, FormInstance, notification, Typography } from 'antd';
import ReusableTable from '../../../../components/TableComponent';
import { FormColumnConfig, MasterConfigs } from '../../../../interfaces/entity';
import { constants } from '../../../../constants/common';
import moment from 'moment';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import { useSelector } from 'react-redux';
import { CheckCircleOutlined, CheckOutlined, CloseOutlined, DownOutlined, FilterOutlined } from '@ant-design/icons'
import Modal from 'antd/es/modal/Modal';
import RenderForm from '../../../../components/Forms/Form';
import { useDispatch } from 'react-redux';
import { postData } from '../../../../actions/developer';
const { Text } = Typography;

const LOPReversal = () => {
    const params = useSelector(({ app }) => app?.params)
    const currentMonth = moment(params?.selectedMonth || moment().format('MMM YYYY'), 'MMM YYYY').format('MM-YYYY')
    const [form, setForm] = useState<FormInstance>(null)
    const [selectedUser, setSelectedUser] = useState(null)
    const [selectedDays, setSelectedDays] = useState([])
    const [manageLOP, setManageLOP] = useState(false)
    const [currentRecord, setCurrentRecord] = useState(null)

    const dispatch = useDispatch()

    const config: MasterConfigs = useMemo(() => ({
        api: () => `/run-payroll/get-lop-reversal`,
        queryParams: {
            month: currentMonth
        },
        tableProps: {
            rowKey: '_id',
            scroll: { y: 600 },
            expandable: {
                expandedRowRender: (record) => {
                    return <div className='flex'>
                        <Table
                            className='w-full mt-2 mb-2 custom-antd-table-p1'
                            rowKey={'_id'}
                            expandable={{
                                expandedRowRender: (lopData) => {
                                    return <div>
                                        <div className='flex justify-end gap-2 mb-4'>
                                            <Button type='primary' onClick={() => {
                                                setSelectedUser(lopData?.userId?._id);
                                                setCurrentRecord(record)
                                                setManageLOP(true)
                                            }} disabled={selectedDays.length == 0}>Manage LOP</Button>
                                        </div>
                                        <Table
                                            className='w-full mt-2 mb-2 custom-antd-table'
                                            rowKey={'_id'}
                                            rowSelection={{
                                                type: 'checkbox',
                                                selections: false,
                                                onChange: (selectedRowKeys, selectedRows) => {
                                                    setSelectedDays(selectedRows.map(ele => ele._id))
                                                },
                                                getCheckboxProps: (lopData: any) => ({
                                                    disabled: lopData?.reversalMonth && lopData?.reversalMonth !== currentMonth, // Column configuration not to be checked
                                                })
                                            }}
                                            columns={getReversalLOPDates()}
                                            dataSource={
                                                lopData?.lopData.map((log) => ({
                                                    key: log._id,
                                                    ...log,
                                                }))
                                            }
                                            scroll={{ y: 300 }
                                            }
                                            pagination={lopData?.lopData?.length > 7 ? {
                                                pageSize: 7,
                                                size: 'small'
                                            } : false}
                                        />
                                    </div>
                                },
                                rowExpandable: (lopData) => lopData?.lopData?.length > 0,
                            }}
                            columns={getReversalLOPMonths()}
                            dataSource={
                                record?.data.map((log) => ({
                                    key: log._id,
                                    ...log,
                                }))
                            }
                            scroll={{ y: 300 }}
                            pagination={false}
                        />
                    </div>
                },
                rowExpandable: (record) => record?.data?.length > 0,
            }
        },
        getColumns: () => employeeColumns as TableColumnProps[],
    }), [params?.selectedMonth, selectedDays])

    const getReversalLOPMonths = (): TableColumnProps[] => [
        {
            title: "Current Month",
            align: 'center',
            dataIndex: "month",
            render: (_) => moment(_, 'MM-YYYY').format('MMM YYYY')
        },
        {
            title: "Working Days",
            align: 'center',
            dataIndex: "totalWorkingDays",
        },
        {
            title: "Present Days",
            align: 'center',
            dataIndex: "presentDays",
        },
        {
            title: "Absent Days",
            align: 'center',
            dataIndex: "absentDays",
        },
        {
            title: "Total Leaves",
            align: 'center',
            dataIndex: "",
            render: (_, record) => parseInt(record?.paidLeavs) + parseInt(record?.unpaidLeaves)
        },
        {
            title: "Unpaid Leaves",
            align: 'center',
            dataIndex: "unpaidLeaves",
        },
        {
            title: "LOP (Confirmed)",
            width: 150,
            align: 'center',
            render: (_, record) => <Text className="font-bold">{record?.lopDays}</Text>
        },
        {
            title: "LOP (Reversed)",
            width: 150,
            align: 'center',
            render: (_, record) => <Text className="font-bold">{record?.totalReversedLOP}</Text>
        }
    ]

    const getReversalLOPDates = (): TableColumnProps[] => [
        {
            title: "Date",
            align: 'center',
            dataIndex: "date",
            render: (_) => moment(_).format('ddd, DD MMM YYYY')
        },
        {
            title: "LOP Type",
            align: 'center',
            dataIndex: "lopType",
        },
        {
            title: "Comment",
            align: 'center',
            dataIndex: "comment",
            ellipsis: true,
            width: 150
        },
        {
            title: "Is Reversed",
            align: 'center',
            dataIndex: "isReversed",
            render: (_) => {
                if (_) {
                    return <CheckOutlined className='text-green-500' />
                } else {
                    return <CloseOutlined className='text-red-500' />
                }
            }
        },
        {
            title: "Reversed Comment",
            align: 'center',
            dataIndex: "reverseComment",
            ellipsis: true,
            width: 200
        },
        {
            title: "Reversed In Month",
            align: 'center',
            dataIndex: "reversalMonth",
            render: (_) => _ ? moment(_, 'MM-YYYY').format('MMM YYYY') : ''
        }
    ]

    const employeeColumns = [
        {
            title: "Employee",
            align: 'left',
            render: (_, record) => <SmallProfileCard
                name={constants.GET_FULL_NAME(record?.userId)}
                avatar={record?.userId?.avatar}
                subTitle={record?.userId?.employeeId}
            />
        },
        {
            title: "Current Month",
            align: 'center',
            dataIndex: "month",
            render: (_) => moment(_, 'MM-YYYY').format('MMM YYYY')
        },
        {
            title: "Working Days",
            align: 'center',
            dataIndex: "totalWorkingDays",
        },
        {
            title: "Present Days",
            align: 'center',
            dataIndex: "presentDays",
        },
        {
            title: "Absent Days",
            align: 'center',
            dataIndex: "absentDays",
        },
        {
            title: "Total Leaves",
            align: 'center',
            dataIndex: "",
            render: (_, record) => parseInt(record?.paidLeavs) + parseInt(record?.unpaidLeaves)
        },
        {
            title: "Unpaid Leaves",
            align: 'center',
            dataIndex: "unpaidLeaves",
        },
        {
            title: "LOP (Confirmed)",
            width: 150,
            align: 'center',
            render: (_, record) => <Text className="font-bold">{record?.lopDays}</Text>
        },
        {
            title: "LOP (Reversed)",
            width: 150,
            align: 'center',
            render: (_, record) => <Text className="font-bold">{record?.totalReversedLOP}</Text>
        }
    ]

    const manageLOPConfig: MasterConfigs = {
        api: () => "/",
        modalTitle: () => "Manage LOP",
        getFormColumns: () => [
            {
                type: "select",
                label: "Reverse Action",
                name: "lopType",
                children: [
                    { name: 'Mark as Reversed', value: 'reverse' },
                    { name: 'Undo Reversal', value: 'removeReverse' },
                ],
                span: 16,
                rules: [{ required: true, message: "Please select a reverse action" }],
            },
            {
                type: "textarea",
                label: "Reason",
                name: "reason",
                rules: [{ required: true, message: "Please enter reverse reason" }],
                span: 16
            },
        ],
        onSubmit: async (id, values) => {
            return await submitReversalLOP(values)
        },
        onRefresh: () => {
            form.resetFields()
            setSelectedUser(null)
            setSelectedDays([])
            setCurrentRecord(null)
            setManageLOP(false)
        }
    }

    const submitReversalLOP = async (data) => {
        try {
            const payload = {
                detailsId: selectedDays,
                comment: data?.reason,
                reversalMonth: currentMonth,
                lopType: data?.lopType
            }

            const resData = await dispatch(postData('/run-payroll/manage-reversal', payload))
            return resData
        } catch (error) {
            return { code: 300, message: 'Failed' }
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
            <Modal
                open={manageLOP}
                onCancel={() => setManageLOP(false)}
                title={"Manage LOP"}
                onOk={() => form && form?.submit()}
                maskClosable={false}
            >
                <RenderForm config={manageLOPConfig} formInstance={setForm} />
            </Modal>

            <ReusableTable config={config} />
        </div>

    );
};

export default LOPReversal;